import { usePublishedData } from "../lib/published-data";
import { useEffect } from "react";
import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router";
import contentStyles from "../styles/content.css?url";
import { LibraryPage } from "../components/content/library-page";
import { DocumentContent } from "../components/document-content";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { useShell } from "../lib/document-route";
import { nativeFeatureQuery } from "../lib/links";
import { keepSearch, searchParams, searchText, type RawSearch } from "../lib/search";
import { reusableFeed } from "../lib/reusable-feed";
import { loadLibrary } from "../server/content";

type LibraryData = Extract<Awaited<ReturnType<typeof loadLibrary>>, { kind: "library" }>;

const library = reusableFeed<LibraryData>();

/** The Library (/library): the page the creator composed, or the native library. */
export const Route = createFileRoute("/library/")({
  // The address stays as written; the library reads its search and collection.
  validateSearch: keepSearch,
  loaderDeps: ({ search }) => ({ native: nativeFeatureQuery("/library", searchParams(search)) }),
  loader: async ({ deps }) => {
    const kept = library.get();
    if (kept) return kept;
    const data = await loadLibrary({ data: deps.native });
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head =
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/library",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          })
        : creatorPageHead(shell, {
            pathname: "/library",
            title: `${loaderData.title} | ${shell.creator.name}`,
            description: loaderData.intro || shell.creator.description,
          });
    return withStylesheet(head, contentStyles);
  },
  component: Library,
});

function Library() {
  const initial = Route.useLoaderData();
  const search = Route.useSearch();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/library", "page");
  const navigate = useNavigate({ from: "/library/" });
  useEffect(() => {
    if (initial.kind === "library") library.keep(initial);
  }, [initial]);
  if (data.kind === "page") return <DocumentContent data={data} shell={shell} />;
  return (
    <LibraryPage
      library={data}
      search={searchText(search, "q")?.trim() ?? ""}
      collection={searchText(search, "collection") ?? ""}
      onSearch={(q) =>
        void navigate({
          search: (previous: RawSearch) => ({ ...previous, q: q || undefined }),
          resetScroll: false,
        })
      }
    />
  );
}
